import { useAuthStore } from "@features/auth/store/authStore";
import { DragDropContext, Droppable, DropResult } from "@hello-pangea/dnd";
import { TestStatus } from "@shared/enums";
import theme from "@shared/utils/themeColors";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useReducer, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import ConfirmationModal from "../../../components/shared/ConfirmationModal";
import AuthenticatedLayout from "../../../shared/layouts/AuthenticatedLayout";
import TestEditorHeader from "../components/TestEditorHeader";
import TestSectionEditor from "../components/TestSectionEditor";
import { TestEditorService } from "../services/test-editor.service";
import { TestDetail, TestSection } from "../types/test-detail.types";

// Define action types for the reducer
type EditorAction =
  | { type: "SET_TEST"; payload: TestDetail }
  | { type: "UPDATE_NAME"; payload: string }
  | {
      type: "UPDATE_SECTION";
      payload: { sectionIndex: number; section: TestSection };
    }
  | { type: "ADD_SECTION"; payload: TestSection }
  | { type: "REMOVE_SECTION"; payload: number }
  | {
      type: "MERGE_SECTIONS";
      payload: { sourceIndex: number; targetIndex: number };
    }
  | {
      type: "CLONE_SECTION";
      payload: number;
    }
  | {
      type: "REORDER_SECTIONS";
      payload: { sourceIndex: number; destinationIndex: number };
    }
  | { type: "UPDATE_STATUS"; payload: TestStatus }
  | { type: "UNDO" }
  | { type: "REDO" }
  | { type: "SAVE_HISTORY" }
  | { type: "SERVER_SAVE"; payload: TestDetail };

// Define the state type
interface EditorState {
  test: TestDetail | null;
  history: TestDetail[];
  currentHistoryIndex: number;
  isDirty: boolean;
  lastSavedIndex: number; // Track the last index that was saved to the server
}

// Initial state
const initialState: EditorState = {
  test: null,
  history: [],
  currentHistoryIndex: -1,
  isDirty: false,
  lastSavedIndex: -1,
};

// Reducer function
function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case "SET_TEST":
      return {
        ...state,
        test: action.payload,
        history: [action.payload],
        currentHistoryIndex: 0,
        isDirty: false,
        lastSavedIndex: 0, // Initial load is considered saved
      };
    case "UPDATE_NAME":
      if (!state.test) return state;
      const updatedTest = {
        ...state.test,
        name: action.payload,
      };
      return {
        ...state,
        test: updatedTest,
        isDirty: true,
      };
    case "UPDATE_STATUS":
      if (!state.test) return state;
      const updatedStatusTest = {
        ...state.test,
        status: action.payload,
      };
      return {
        ...state,
        test: updatedStatusTest,
        isDirty: true,
      };
    case "UPDATE_SECTION":
      if (!state.test) return state;
      const updatedSections = [...state.test.sections];
      updatedSections[action.payload.sectionIndex] = action.payload.section;
      return {
        ...state,
        test: {
          ...state.test,
          sections: updatedSections,
        },
        isDirty: true,
      };
    case "ADD_SECTION":
      if (!state.test) return state;
      return {
        ...state,
        test: {
          ...state.test,
          sections: [...state.test.sections, action.payload],
        },
        isDirty: true,
      };
    case "REMOVE_SECTION":
      if (!state.test) return state;
      const sectionsAfterRemoval = [...state.test.sections];
      sectionsAfterRemoval.splice(action.payload, 1);
      return {
        ...state,
        test: {
          ...state.test,
          sections: sectionsAfterRemoval,
        },
        isDirty: true,
      };
    case "MERGE_SECTIONS":
      if (!state.test) return state;
      const { sourceIndex, targetIndex } = action.payload;
      const sourceSections = [...state.test.sections];
      const sourceSection = sourceSections[sourceIndex];
      const targetSection = sourceSections[targetIndex];

      // Merge the sections
      const mergedSection: TestSection = {
        ...targetSection,
        passages: [...targetSection.passages, ...sourceSection.passages],
        questions: [...targetSection.questions, ...sourceSection.questions],
      };

      // Replace target with merged section and remove source
      sourceSections[targetIndex] = mergedSection;
      sourceSections.splice(sourceIndex, 1);

      return {
        ...state,
        test: {
          ...state.test,
          sections: sourceSections,
        },
        isDirty: true,
      };
    case "CLONE_SECTION":
      if (!state.test) return state;
      const clonedSection: TestSection = JSON.parse(
        JSON.stringify(state.test.sections[action.payload])
      );
      clonedSection.id = uuidv4();

      const sectionsWithClone = [...state.test.sections];
      sectionsWithClone.splice(action.payload + 1, 0, clonedSection);

      return {
        ...state,
        test: {
          ...state.test,
          sections: sectionsWithClone,
        },
        isDirty: true,
      };
    case "REORDER_SECTIONS":
      if (!state.test) return state;
      const { sourceIndex: fromIndex, destinationIndex: toIndex } =
        action.payload;
      const reorderedSections = [...state.test.sections];

      // Remove the section from the original position
      const [movedSection] = reorderedSections.splice(fromIndex, 1);

      // Insert the section at the new position
      reorderedSections.splice(toIndex, 0, movedSection);

      return {
        ...state,
        test: {
          ...state.test,
          sections: reorderedSections,
        },
        isDirty: true,
      };
    case "SAVE_HISTORY":
      if (!state.test || !state.isDirty) return state;

      // Remove any future history if we've gone back and made changes
      const newHistory = state.history.slice(0, state.currentHistoryIndex + 1);
      newHistory.push(JSON.parse(JSON.stringify(state.test)));

      // Keep isDirty true because we still need to save to the server
      // This is just saving to local history
      return {
        ...state,
        history: newHistory,
        currentHistoryIndex: newHistory.length - 1,
        isDirty: true, // Keep it dirty until saved to server
      };
    case "SERVER_SAVE":
      // When we save to the server, we update the test data and mark as not dirty
      // We also need to update the history with the latest server state
      const serverHistory = [...state.history];
      serverHistory[state.currentHistoryIndex] = JSON.parse(
        JSON.stringify(action.payload)
      );

      return {
        ...state,
        test: action.payload,
        history: serverHistory,
        isDirty: false, // Not dirty after server save
        lastSavedIndex: state.currentHistoryIndex, // Update the last saved index
      };
    case "UNDO":
      if (state.currentHistoryIndex <= 0) return state;

      const previousIndex = state.currentHistoryIndex - 1;
      const previousState = state.history[previousIndex];

      // When we undo, we need to check if this state needs to be saved
      // If the current index is different from the last saved index, it needs to be saved
      return {
        ...state,
        test: JSON.parse(JSON.stringify(previousState)),
        currentHistoryIndex: previousIndex,
        isDirty: previousIndex !== state.lastSavedIndex,
      };
    case "REDO":
      if (state.currentHistoryIndex >= state.history.length - 1) return state;

      const nextIndex = state.currentHistoryIndex + 1;
      const nextState = state.history[nextIndex];

      // When we redo, we need to check if this state needs to be saved
      // If the current index is different from the last saved index, it needs to be saved
      return {
        ...state,
        test: JSON.parse(JSON.stringify(nextState)),
        currentHistoryIndex: nextIndex,
        isDirty: nextIndex !== state.lastSavedIndex,
      };
    default:
      return state;
  }
}

// Utility function to preserve scroll position during updates
export function preserveScrollPosition(callback: () => void) {
  // Find the scrollable container by its ID
  const scrollContainer = document.getElementById(
    "test-editor-scroll-container"
  );

  // Store current scroll position
  const scrollTop = scrollContainer ? scrollContainer.scrollTop : 0;

  // Execute the callback that will update state
  callback();

  // Use requestAnimationFrame to restore scroll position after DOM updates
  requestAnimationFrame(() => {
    // Double requestAnimationFrame to ensure DOM is fully updated
    requestAnimationFrame(() => {
      // Restore scroll position on the specific container
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollTop;
      }
    });
  });
}

export default function TestEditor() {
  const { testId } = useParams<{ testId: string }>();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(editorReducer, initialState);
  const [showUnsavedChanges, setShowUnsavedChanges] = useState(false);
  const [showMergeConfirmation, setShowMergeConfirmation] = useState<{
    show: boolean;
    sourceIndex: number;
    targetIndex: number;
  }>({ show: false, sourceIndex: -1, targetIndex: -1 });

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<{
    show: boolean;
    sectionIndex: number;
  }>({ show: false, sectionIndex: -1 });
  // State to control if sections are expanded or collapsed
  const [sectionsExpanded, setSectionsExpanded] = useState<boolean>(true);
  // Unique key to force re-rendering when expansion state changes
  const [expandCollapseKey, setExpandCollapseKey] = useState<number>(0);

  // Optimized function to expand/collapse all sections
  const toggleSectionsExpanded = (expanded: boolean) => {
    // Use the utility function to preserve scroll position during updates
    preserveScrollPosition(() => {
      // Update expansion state
      setSectionsExpanded(expanded);
      // Increment key to force re-rendering of child components
      setExpandCollapseKey((prev) => prev + 1);
    });
  };

  // Handle drag end event from DragDropContext
  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    // If dropped outside the list or at the same position
    if (
      !destination ||
      (source.droppableId === destination.droppableId &&
        source.index === destination.index)
    ) {
      return;
    }

    // Reorder sections
    dispatch({
      type: "REORDER_SECTIONS",
      payload: {
        sourceIndex: source.index,
        destinationIndex: destination.index,
      },
    });

    // Save history after reordering
    dispatch({ type: "SAVE_HISTORY" });
  };

  // Fetch test data
  const {
    data: test,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["test-edit", testId],
    queryFn: () => TestEditorService.getTestForEditing(testId!),
    enabled: !!testId,
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  // Initialize state with fetched test
  useEffect(() => {
    if (test) {
      dispatch({ type: "SET_TEST", payload: test });
    }
  }, [test]);

  // Save history when changes are made
  useEffect(() => {
    if (state.isDirty) {
      const timer = setTimeout(() => {
        // We're only saving to history, not to the server
        // so we use a new action type that doesn't affect isDirty
        dispatch({ type: "SAVE_HISTORY" });
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [state.isDirty, state.test]);

  // Effect to handle beforeunload event to prevent accidental window closing
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (state.isDirty) {
        // Standard way to show a confirmation dialog before closing the window
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [state.isDirty]);

  // Save test mutation
  const { mutate: saveTest, isPending: isSaving } = useMutation({
    mutationFn: (testData: Partial<TestDetail>) =>
      TestEditorService.saveTest(testId!, testData),
    onSuccess: (updatedTest) => {
      queryClient.setQueryData(["test-edit", testId], updatedTest);

      // When we successfully save to the server, we need to:
      // 1. Update the test data
      // 2. Create a new action type to handle server saves
      dispatch({ type: "SERVER_SAVE", payload: updatedTest });
    },
  });

  // Handle save
  const handleSave = () => {
    if (!state.test) return;

    saveTest(state.test);

    // After saving to the server, we need to update the isDirty state
    // This will be handled in the onSuccess callback of the mutation
  };

  // Handle undo
  const handleUndo = () => {
    dispatch({ type: "UNDO" });
  };

  // Handle redo
  const handleRedo = () => {
    dispatch({ type: "REDO" });
  };

  // Handle test name change
  const handleNameChange = (name: string) => {
    dispatch({ type: "UPDATE_NAME", payload: name });
  };

  // Handle test status change
  const handleStatusChange = (isPublished: boolean) => {
    dispatch({
      type: "UPDATE_STATUS",
      payload: isPublished ? TestStatus.PUBLISHED : TestStatus.DRAFT,
    });
  };

  // Handle section update
  const handleSectionUpdate = (sectionIndex: number, section: TestSection) => {
    dispatch({
      type: "UPDATE_SECTION",
      payload: { sectionIndex, section },
    });
  };

  // Handle section add
  const handleAddSection = () => {
    const newSection: TestSection = {
      id: uuidv4(),
      passages: [],
      questions: [],
      sectionNumber: 1,
    };

    dispatch({ type: "ADD_SECTION", payload: newSection });
  };

  // Handle section remove
  const handleRemoveSection = (sectionIndex: number) => {
    setShowDeleteConfirmation({
      show: true,
      sectionIndex,
    });
  };

  // Confirm section delete
  const confirmDeleteSection = () => {
    dispatch({
      type: "REMOVE_SECTION",
      payload: showDeleteConfirmation.sectionIndex,
    });
    setShowDeleteConfirmation({ show: false, sectionIndex: -1 });
  };

  // Handle section merge
  const handleMergeSections = (sourceIndex: number, targetIndex: number) => {
    setShowMergeConfirmation({
      show: true,
      sourceIndex,
      targetIndex,
    });
  };

  // Confirm section merge
  const confirmMergeSections = () => {
    dispatch({
      type: "MERGE_SECTIONS",
      payload: {
        sourceIndex: showMergeConfirmation.sourceIndex,
        targetIndex: showMergeConfirmation.targetIndex,
      },
    });

    setShowMergeConfirmation({ show: false, sourceIndex: -1, targetIndex: -1 });
  };

  // Handle section clone
  const handleCloneSection = (sectionIndex: number) => {
    dispatch({ type: "CLONE_SECTION", payload: sectionIndex });
  };

  // Handle navigation away with unsaved changes
  const handleNavigationAttempt = () => {
    if (state.isDirty) {
      setShowUnsavedChanges(true);
      return;
    }

    navigate("/test-processing");
  };

  if (isLoading) {
    return (
      <AuthenticatedLayout defaultCollapsed={true}>
        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
            <div className="h-96 bg-gray-200 rounded mb-4"></div>
            <div className="h-96 bg-gray-200 rounded"></div>
          </div>
        </div>
      </AuthenticatedLayout>
    );
  }

  if (error || !state.test) {
    return (
      <AuthenticatedLayout defaultCollapsed={true}>
        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Test Not Found</h2>
            <p className={`mt-2 ${theme.text.muted}`}>
              The test you are trying to edit does not exist or you don't have
              access to it.
            </p>
            <button
              onClick={() => navigate("/test-processing")}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-violet-600 hover:bg-violet-700"
            >
              Back to List
            </button>
          </div>
        </div>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout defaultCollapsed={true} className="overflow-y-hidden">
      {/* Header with test name and controls - Now fixed */}
      <TestEditorHeader
        testName={state.test.name}
        onNameChange={handleNameChange}
        onSave={handleSave}
        onUndo={handleUndo}
        onRedo={handleRedo}
        onBack={handleNavigationAttempt}
        canUndo={state.currentHistoryIndex > 0}
        canRedo={state.currentHistoryIndex < state.history.length - 1}
        isSaving={isSaving}
        isDirty={state.isDirty}
        pdfUrl={state.test.pdfUrl}
        isPublished={state.test.status === TestStatus.PUBLISHED}
        onPublishChange={handleStatusChange}
        onExpandAll={() => toggleSectionsExpanded(true)}
        onCollapseAll={() => toggleSectionsExpanded(false)}
      />

      {/* Main container with padding-top to compensate for the fixed header */}
      <div
        id="test-editor-scroll-container"
        className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto overflow-y-auto h-[calc(100%-120px)]"
      >
        {/* Sections with Drag and Drop */}
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="test-sections">
            {(provided) => (
              <div
                className="space-y-8"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {state.test.sections.map((section, index) => (
                  <TestSectionEditor
                    key={`section-${section.id}-${expandCollapseKey}`}
                    section={section}
                    sectionIndex={index}
                    onUpdate={(updatedSection) =>
                      handleSectionUpdate(index, updatedSection)
                    }
                    onRemove={() => handleRemoveSection(index)}
                    onMerge={(targetIndex) =>
                      handleMergeSections(index, targetIndex)
                    }
                    onClone={() => handleCloneSection(index)}
                    testId={testId}
                    otherSections={state.test!.sections.filter(
                      (_, i) => i !== index
                    )}
                    userId={user?.uid || ""}
                    isExpanded={sectionsExpanded}
                  />
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {/* Add Section Button */}
        <div className="flex justify-center mt-8">
          <button
            onClick={handleAddSection}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              ></path>
            </svg>
            Add Section
          </button>
        </div>

        {/* Unsaved Changes Confirmation Modal */}
        <ConfirmationModal
          isOpen={showUnsavedChanges}
          onClose={() => setShowUnsavedChanges(false)}
          onConfirm={async () => {
            setShowUnsavedChanges(false);
            navigate("/test-processing");
          }}
          isLoading={false}
          title="Unsaved Changes"
          message="You have unsaved changes. Are you sure you want to leave this page? All unsaved changes will be lost."
          confirmText="Leave"
          cancelText="Stay"
          icon={
            <svg
              className={`shrink-0 fill-current ${theme.text.warning}`}
              width="16"
              height="16"
              viewBox="0 0 16 16"
            >
              <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm0 12c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm1-3H7V4h2v5z" />
            </svg>
          }
        />

        {/* Merge Sections Confirmation Modal */}
        <ConfirmationModal
          isOpen={showMergeConfirmation.show}
          onClose={() =>
            setShowMergeConfirmation({
              show: false,
              sourceIndex: -1,
              targetIndex: -1,
            })
          }
          onConfirm={async () => confirmMergeSections()}
          isLoading={false}
          title="Merge Sections"
          message={`Are you sure you want to merge Section ${
            showMergeConfirmation.sourceIndex + 1
          } into Section ${
            showMergeConfirmation.targetIndex + 1
          }? This action cannot be undone.`}
          confirmText="Merge"
          cancelText="Cancel"
          icon={
            <svg
              className={`shrink-0 fill-current ${theme.text.warning}`}
              width="16"
              height="16"
              viewBox="0 0 16 16"
            >
              <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm0 12c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm1-3H7V4h2v5z" />
            </svg>
          }
        />

        {/* Delete Section Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteConfirmation.show}
          onClose={() =>
            setShowDeleteConfirmation({
              show: false,
              sectionIndex: -1,
            })
          }
          onConfirm={async () => confirmDeleteSection()}
          isLoading={false}
          title="Delete Section"
          message={`Are you sure you want to delete Section ${
            showDeleteConfirmation.sectionIndex + 1
          }? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          icon={
            <svg
              className={`shrink-0 fill-current ${theme.text.error}`}
              width="16"
              height="16"
              viewBox="0 0 16 16"
            >
              <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm0 12c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm1-3H7V4h2v5z" />
            </svg>
          }
        />
      </div>
    </AuthenticatedLayout>
  );
}
